/* eslint-disable max-lines-per-function, complexity, max-len */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@vehix/portal/components/ui/button';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Input } from '@vehix/portal/components/ui/input';
import { Textarea } from '@vehix/portal/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@vehix/portal/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import {
  AlertCircle,
  Building2,
  Check,
  CheckCircle,
  Mail,
  User as UserIcon,
  UserPlus
} from 'lucide-react';
import type { Tenant } from '@vehix/portal/services/api/tenants';
import { SystemRole } from '@vehix/portal/types';
import { isValidEmail, normalizeEmail } from '../utils/emailIdentity';
import {
  buildDomainTenantIndex,
  sortTenantsBySuggestion,
  suggestTenantForEmail
} from '../utils/tenantInference';
import type { Role, UserApprovalRecord } from './UserApprovalsTab';

/**
 * Add User dialog.
 *
 * Lives in its own component for two reasons:
 *
 *  1. Performance. Every keystroke updates form state, and while this markup
 *     was inline in UserApprovalsTab that re-rendered the entire user table —
 *     up to 100 rows, each carrying two Radix Selects and a dropdown menu.
 *     Isolating the state here means typing re-renders only the dialog.
 *  2. The validation and tenant-inference logic is self-contained, so keeping
 *     it beside the markup it drives is easier to follow.
 *
 * The parent still owns `open` and performs the actual create.
 */

export interface NewUserFormValues {
  /** Already normalized (trimmed + lowercased). */
  email: string;
  /** Already trimmed. */
  firstName: string;
  /** Already trimmed. */
  lastName: string;
  roleId: string;
  tenantId: string;
  /** Already trimmed; empty string when the admin left it blank. */
  notes: string;
}

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: Role[];
  rolesError: string | null;
  allTenants: Tenant[] | undefined;
  allTenantsLoading: boolean;
  allTenantsError: unknown;
  /** Existing users — the source for both duplicate detection and tenant inference. */
  approvals: UserApprovalRecord[];
  currentUserEmail: string | undefined;
  creating: boolean;
  /** Performs the create. Resolve true to clear the form, false to keep it for a retry. */
  onCreate: (values: NewUserFormValues) => Promise<boolean>;
  onResendInvite: (approval: UserApprovalRecord) => void;
  onViewUser: (approval: UserApprovalRecord) => void;
}

interface FieldErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  tenant?: string;
}

export const AddUserDialog: React.FC<AddUserDialogProps> = ({
  open,
  onOpenChange,
  roles,
  rolesError,
  allTenants,
  allTenantsLoading,
  allTenantsError,
  approvals,
  currentUserEmail,
  creating,
  onCreate,
  onResendInvite,
  onViewUser
}) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  // Set once the admin picks a tenant by hand. Domain inference must never
  // overwrite a deliberate choice, so auto-fill only runs while this is false.
  const [tenantTouched, setTenantTouched] = useState(false);

  // -------------------------------------------------------------------
  // Derived state. Everything below is memoized on inputs that do NOT
  // change per keystroke, except the two O(1) lookups keyed on `email`.
  // -------------------------------------------------------------------

  /** domain -> tenant suggestion. Rebuilt only when the source lists change. */
  const domainTenantIndex = useMemo(
    () => buildDomainTenantIndex(approvals, allTenants),
    [approvals, allTenants]
  );

  /**
   * Normalized email -> approval, so the per-keystroke duplicate check is a
   * single Map lookup instead of a linear scan that re-lowercases every
   * stored email on every character.
   */
  const approvalsByEmail = useMemo(() => {
    const byEmail = new Map<string, UserApprovalRecord>();
    for (const approval of approvals) {
      byEmail.set(normalizeEmail(approval.email), approval);
    }
    return byEmail;
  }, [approvals]);

  const normalizedEmail = normalizeEmail(email);

  /**
   * The existing user matching the typed email, if any. O(1).
   *
   * This is the guard for the bug this dialog was built around: re-adding an
   * existing user used to sail past the "already exists" check — GoTrue
   * re-invites unconfirmed users rather than erroring — and then die on the
   * duplicate insert into user_approvals. The collision is now visible before
   * submit.
   */
  const duplicateApproval = normalizedEmail ? approvalsByEmail.get(normalizedEmail) : undefined;

  /** Returns the same object identity for an unchanged domain, so the memos below stay stable. */
  const tenantSuggestion = useMemo(
    () => (email ? suggestTenantForEmail(email, domainTenantIndex) : undefined),
    [email, domainTenantIndex]
  );

  const orderedTenants = useMemo(
    () => sortTenantsBySuggestion(allTenants || [], tenantSuggestion),
    [allTenants, tenantSuggestion]
  );

  const suggestedTenantIds = useMemo(
    () => new Set(tenantSuggestion?.tenantIds || []),
    [tenantSuggestion]
  );

  const duplicateTenant = useMemo(() => {
    if (!duplicateApproval) return undefined;
    const id = duplicateApproval.tenant_id || duplicateApproval.user_tenants?.[0]?.id;
    return (allTenants || []).find(t => t.id === id);
  }, [duplicateApproval, allTenants]);

  /** Adding yourself is always a mistake — you already have an account. */
  const isSelfAdd =
    Boolean(currentUserEmail) && normalizedEmail === normalizeEmail(currentUserEmail || '');

  /**
   * Only a super_admin may mint another super_admin. Without this the role
   * dropdown offered privilege escalation to anyone who reached this page.
   */
  const actingUserIsSuperAdmin = useMemo(
    () =>
      approvals.some(
        a =>
          normalizeEmail(a.email) === normalizeEmail(currentUserEmail || '') &&
          a.user_role?.role_name === SystemRole.SUPER_ADMIN
      ),
    [approvals, currentUserEmail]
  );

  const assignableRoles = useMemo(
    () => (actingUserIsSuperAdmin ? roles : roles.filter(r => r.name !== SystemRole.SUPER_ADMIN)),
    [roles, actingUserIsSuperAdmin]
  );

  const suggestedTenantName = useMemo(() => {
    if (!tenantSuggestion?.confident) return undefined;
    const match = orderedTenants.find(t => t.id === tenantSuggestion.tenantIds[0]);
    return match?.name || match?.tenant_name;
  }, [tenantSuggestion, orderedTenants]);

  /** Auto-fill the tenant when the domain maps to exactly one, unless overridden. */
  useEffect(() => {
    if (!open || tenantTouched) return;
    const suggested = tenantSuggestion?.confident ? tenantSuggestion.tenantIds[0] : undefined;
    if (suggested) setTenantId(prev => (prev === suggested ? prev : suggested));
  }, [open, tenantTouched, tenantSuggestion]);

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setRoleId('');
    setTenantId('');
    setNotes('');
    setTenantTouched(false);
    setErrors({});
  };

  /**
   * Drops a single field error. Deletes the key rather than assigning
   * undefined — this project runs with exactOptionalPropertyTypes, under
   * which `{ ...prev, email: undefined }` is not a valid optional field.
   */
  const clearError = (field: keyof FieldErrors) => {
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};

    if (!normalizedEmail) {
      next.email = 'Email is required.';
    } else if (!isValidEmail(normalizedEmail)) {
      next.email = 'Enter a valid email address, for example name@dealership.com.';
    } else if (isSelfAdd) {
      next.email = 'This is your own account — you already have access.';
    } else if (duplicateApproval) {
      next.email = 'This user already exists. Use Resend invite or View user instead.';
    }

    if (!firstName.trim()) next.firstName = 'First name is required.';
    if (!lastName.trim()) next.lastName = 'Last name is required.';
    if (!roleId) next.role = 'Select a role.';
    if (!tenantId) next.tenant = 'Select a tenant.';

    return next;
  };

  const handleSubmit = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const succeeded = await onCreate({
      email: normalizedEmail,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      roleId,
      tenantId,
      notes: notes.trim()
    });

    // On failure keep every value so the admin can correct and retry.
    if (succeeded) resetForm();
  };

  const submitDisabled =
    creating ||
    !normalizedEmail ||
    !firstName.trim() ||
    !lastName.trim() ||
    !roleId ||
    !tenantId ||
    // Blocked states — the create would fail or is a mistake
    !isValidEmail(normalizedEmail) ||
    Boolean(duplicateApproval) ||
    isSelfAdd;

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) resetForm();
        onOpenChange(next);
      }}
    >
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            <span className='flex items-center gap-2'>
              <UserPlus className='h-5 w-5' />
              Add New User
            </span>
          </DialogTitle>
          <DialogDescription>
            Create a new user account using Supabase&apos;s invite flow. An invitation email will be
            sent to the user with a link to set their password. The user will be approved
            immediately and can sign in once they set their password.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          {/* Email */}
          <div className='space-y-2'>
            <label htmlFor='new-user-email' className='text-sm font-medium'>
              Email <span className='text-red-600'>*</span>
            </label>
            <Input
              id='new-user-email'
              type='email'
              placeholder='user@example.com'
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(e.target.value);
                clearError('email');
              }}
              aria-invalid={Boolean(errors.email)}
              required
            />

            {/* Duplicate guard. Catches the case that used to fail at the
                database: re-adding someone who already exists. */}
            {duplicateApproval ? (
              <div className='rounded border border-amber-500/40 bg-amber-500/10 p-3 space-y-2'>
                <div className='flex items-start gap-2'>
                  <AlertCircle className='h-4 w-4 mt-0.5 shrink-0 text-amber-600' />
                  <div className='space-y-1 text-sm'>
                    <p className='font-medium'>This user already exists</p>
                    <p className='text-muted-foreground'>
                      {duplicateApproval.full_name || duplicateApproval.email}
                      {duplicateTenant &&
                        ` · ${duplicateTenant.name || duplicateTenant.tenant_name}`}
                      {duplicateApproval.user_role?.role_display_name &&
                        ` · ${duplicateApproval.user_role.role_display_name}`}
                    </p>
                    <p className='text-muted-foreground'>
                      {duplicateApproval.email_confirmed_at
                        ? 'Status: Active'
                        : 'Status: Invited — has not accepted yet'}
                    </p>
                  </div>
                </div>
                <div className='flex gap-2'>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    onClick={() => {
                      onOpenChange(false);
                      onResendInvite(duplicateApproval);
                      resetForm();
                    }}
                  >
                    <Mail className='h-3 w-3 mr-1' />
                    Resend invite
                  </Button>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    onClick={() => {
                      onOpenChange(false);
                      onViewUser(duplicateApproval);
                      resetForm();
                    }}
                  >
                    <UserIcon className='h-3 w-3 mr-1' />
                    View user
                  </Button>
                </div>
              </div>
            ) : errors.email ? (
              <p className='text-xs text-destructive'>{errors.email}</p>
            ) : (
              <p className='text-xs text-muted-foreground'>
                User will receive an email invitation to set their password at portal.vehix.ai
              </p>
            )}
          </div>

          {/* First Name */}
          <div className='space-y-2'>
            <label htmlFor='new-user-first-name' className='text-sm font-medium'>
              First Name <span className='text-red-600'>*</span>
            </label>
            <Input
              id='new-user-first-name'
              type='text'
              placeholder='John'
              value={firstName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFirstName(e.target.value);
                clearError('firstName');
              }}
              aria-invalid={Boolean(errors.firstName)}
              required
            />
            {errors.firstName && <p className='text-xs text-destructive'>{errors.firstName}</p>}
          </div>

          {/* Last Name */}
          <div className='space-y-2'>
            <label htmlFor='new-user-last-name' className='text-sm font-medium'>
              Last Name <span className='text-red-600'>*</span>
            </label>
            <Input
              id='new-user-last-name'
              type='text'
              placeholder='Doe'
              value={lastName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setLastName(e.target.value);
                clearError('lastName');
              }}
              aria-invalid={Boolean(errors.lastName)}
              required
            />
            {errors.lastName && <p className='text-xs text-destructive'>{errors.lastName}</p>}
          </div>

          {/* Role Selection */}
          <div className='space-y-2'>
            <label htmlFor='new-user-role' className='text-sm font-medium'>
              Role <span className='text-red-600'>*</span>
            </label>
            {rolesError ? (
              <div className='text-sm text-destructive p-2 border border-destructive rounded'>
                <AlertCircle className='h-4 w-4 inline mr-2' />
                Roles could not be loaded, so no user can be created right now. Refresh the page,
                and if it persists check that your account still has admin access.
              </div>
            ) : (
              <Select
                // Conditional spread: omitting `value` entirely is what makes
                // Radix fall back to the placeholder. Passing an explicit
                // undefined is rejected under exactOptionalPropertyTypes.
                {...(roleId.trim() !== '' && { value: roleId })}
                onValueChange={(value: string) => {
                  setRoleId(value);
                  clearError('role');
                }}
                required
              >
                <SelectTrigger id='new-user-role' className='w-full'>
                  <SelectValue
                    placeholder={
                      assignableRoles.length === 0
                        ? 'Loading roles...'
                        : 'Select a role for this user...'
                    }
                  />
                </SelectTrigger>
                <SelectContent className='z-[10000]'>
                  {assignableRoles.length === 0 ? (
                    <SelectItem value='loading' disabled>
                      Loading roles...
                    </SelectItem>
                  ) : (
                    assignableRoles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        <div className='flex flex-col'>
                          <span className='font-medium'>{role.display_name}</span>
                          {role.description && (
                            <span className='text-xs text-muted-foreground'>
                              {role.description}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
            {errors.role ? (
              <p className='text-xs text-destructive'>{errors.role}</p>
            ) : (
              <p className='text-xs text-muted-foreground'>
                This determines what permissions and access the user will have in the system.
              </p>
            )}
          </div>

          {/* Tenant Selection (single tenant only) */}
          <div className='space-y-2'>
            <label htmlFor='new-user-tenant' className='text-sm font-medium'>
              Tenant <span className='text-red-600'>*</span>
            </label>
            {allTenantsLoading ? (
              <div className='flex items-center justify-center py-4'>
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary' />
                <span className='ml-2 text-sm'>Loading tenants...</span>
              </div>
            ) : allTenantsError ? (
              <div className='text-sm text-destructive p-2 border border-destructive rounded'>
                <AlertCircle className='h-4 w-4 inline mr-2' />
                Error loading tenants. Please refresh the page.
              </div>
            ) : orderedTenants.length > 0 ? (
              <Select
                {...(tenantId && { value: tenantId })}
                onValueChange={(value: string) => {
                  // Mark as chosen by hand so domain inference stops
                  // overriding it on later email edits.
                  setTenantTouched(true);
                  setTenantId(value);
                  clearError('tenant');
                }}
              >
                <SelectTrigger id='new-user-tenant'>
                  <SelectValue placeholder='Select the tenant for this user' />
                </SelectTrigger>
                <SelectContent>
                  {orderedTenants.map(tenant => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      <div className='flex items-center gap-2'>
                        <Building2 className='h-3 w-3 text-muted-foreground' />
                        <span>{tenant.name || tenant.tenant_name}</span>
                        {tenant.tenant_slug && (
                          <Badge variant='outline' className='text-xs'>
                            {tenant.tenant_slug}
                          </Badge>
                        )}
                        {suggestedTenantIds.has(tenant.id) && (
                          <Badge variant='secondary' className='text-xs'>
                            Suggested
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className='text-sm text-destructive p-2 border border-destructive rounded'>
                <AlertCircle className='h-4 w-4 inline mr-2' />
                No tenants available, so no user can be created. Create a tenant first in Tenant
                Setup.
              </div>
            )}

            {/* Domain-based tenant hint. Suggestion only — the dropdown above
                stays editable and a manual pick always wins. */}
            {errors.tenant ? (
              <p className='text-xs text-destructive'>{errors.tenant}</p>
            ) : suggestedTenantName && !tenantTouched ? (
              <p className='text-xs text-muted-foreground'>
                <Check className='h-3 w-3 inline mr-1 text-emerald-600' />
                Set to <span className='font-medium'>{suggestedTenantName}</span> based on the email
                domain. Change it if that&apos;s not right.
              </p>
            ) : tenantSuggestion && !tenantSuggestion.confident ? (
              <p className='text-xs text-muted-foreground'>
                This domain is used by {tenantSuggestion.tenantIds.length} tenants — the likely ones
                are listed first. Please confirm which is correct.
              </p>
            ) : (
              <p className='text-xs text-muted-foreground'>
                Select the tenant for this user. They will be assigned to this tenant only.
              </p>
            )}
          </div>

          {/* Notes */}
          <div className='space-y-2'>
            <label htmlFor='new-user-notes' className='text-sm font-medium'>
              Notes (Optional)
            </label>
            <Textarea
              id='new-user-notes'
              placeholder='Add any additional notes about this user...'
              value={notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => {
              onOpenChange(false);
              resetForm();
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            {creating ? (
              <>
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                Creating...
              </>
            ) : (
              <>
                <CheckCircle className='h-4 w-4 mr-2' />
                Create User
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
